/**
 * Which dictionary is fetched, and when.
 *
 * The rule the bundle layout has to respect (`src/ui/README.md` §10): the
 * overlay and the board paint off ONE fetch. So exactly one dictionary is a
 * static import. Ukrainian core, the default locale of the default surface,
 * and everything else is a chunk of its own:
 *
 *   uk/core   static, in the main chunk, and the fallback table
 *   en/core   fetched before the app mounts, only when the locale is English
 *   uk/staff  fetched by the route loader on the five gated surfaces
 *   en/staff  same, in English
 *
 * A spectator arriving by QR code therefore pays nothing at all, and an English
 * reader pays one small fetch before first paint rather than a flash of
 * Ukrainian. `main.tsx` awaits `bootLocale()` before `createRoot().render()`,
 * which is what buys that.
 */

import type { Locale } from "../../shared/settings";
import { core as ukCore } from "./messages/uk/core";
import {
  applyLocale,
  type BundleName,
  type Dictionary,
  getLocale,
  hasBundle,
  installBundle,
  setFallback,
} from "./runtime";

type Loader = () => Promise<Dictionary>;

/**
 * The bundles a PAGE can ask for.
 *
 * `push` is a bundle too, and it is deliberately not in here: the service
 * worker imports both of its locales statically and installs them itself,
 * because a worker woken by a push may have no network to fetch one with. A
 * loader for it would emit a chunk nothing ever requests — and, worse, would
 * make `switchLocale` fetch it on every language change.
 */
type PageBundle = Exclude<BundleName, "push">;

const LOADERS: Record<Locale, Record<PageBundle, Loader>> = {
  uk: {
    core: () => Promise.resolve(ukCore),
    staff: () => import("./messages/uk/staff").then((m) => m.staff),
  },
  en: {
    core: () => import("./messages/en/core").then((m) => m.core),
    staff: () => import("./messages/en/staff").then((m) => m.staff),
  },
};

/** Bundles this session has asked for, so a locale switch reloads all of them
 *  rather than leaving the admin half-translated. */
const wanted = new Set<PageBundle>(["core"]);

setFallback(ukCore);
installBundle("uk", "core", ukCore);

async function fetchBundle(locale: Locale, name: PageBundle): Promise<void> {
  if (hasBundle(locale, name)) return;
  installBundle(locale, name, await LOADERS[locale][name]());
}

/**
 * Make sure a bundle is present for the current locale. TanStack Router's
 * `loader` awaits this on the gated routes, so the dictionary and the route's
 * own chunk are in flight together.
 */
export async function loadBundle(name: PageBundle): Promise<void> {
  wanted.add(name);
  await fetchBundle(getLocale(), name);
}

/** Fetch everything this session is using in the new locale, THEN switch, so
 *  the swap is atomic and no surface renders half-translated. */
export async function switchLocale(next: Locale): Promise<void> {
  if (next === getLocale()) return;
  await Promise.all([...wanted].map((name) => fetchBundle(next, name)));
  applyLocale(next);
}

/** First paint. Resolves once the chosen locale's core is in memory. */
export async function bootLocale(locale: Locale): Promise<void> {
  if (locale === "uk") return;
  await fetchBundle(locale, "core");
  applyLocale(locale);
}
